import * as React from 'react';
import * as PropTypes from 'prop-types';

import { merge } from 'bernie-core';
const { withoutKeys } = merge;
import * as queue from './image-queue';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';

export interface QueuedImageProps {
  alt?: string;
  src: string;
  className?: string;
  groupId?: string; // used for image queueing context
  useBackgroundImage?: boolean;
  queuedImageFallbackCallback?: (queuedImage: object) => void;
  ratio?: UitkFigureAspectRatioType;
  serverRenderCallback?: (style: object) => object;
  serverRender?: boolean; // do not use queue
  loaded?: boolean;
  lazyLoad?: boolean;
}

export interface QueuedImageState {
  src: string;
}

export class QueuedImage extends React.PureComponent<QueuedImageProps, QueuedImageState> {
  private imgRef;
  private passThroughProps;

  public static propTypes = {
    src: PropTypes.string.isRequired, // url
    className: PropTypes.string,
    groupId: PropTypes.string, // used for image queueing context
    useBackgroundImage: PropTypes.bool,
    queuedImageFallbackCallback: PropTypes.func,
    ratio: PropTypes.string,
    serverRenderCallback: PropTypes.func,
    serverRender: PropTypes.bool, // do not use queue
    loaded: PropTypes.bool,
    lazyLoad: PropTypes.bool
  };

  public static defaultProps = {
    useBackgroundImage: false,
    lazyLoad: false
  };

  constructor(props) {
    super(props);
    this.state = {
      src: undefined
    };

    this.setImgRef = this.setImgRef.bind(this);
    this.passThroughProps = this._getPassthroughProps(props);
  }

  public componentWillReceiveProps(nextProps) {
    if (this.props.src !== nextProps.src) {
      this.loadImage(nextProps.src);
    }
    this.passThroughProps = this._getPassthroughProps(nextProps);
  }

  public componentWillMount() {
    if (this.props.serverRender && this.props.src) {
      this.setState({ src: this.props.src });
    }
  }

  public componentDidMount() {
    if (!this.state.src) {
      this.loadImage(this.props.src);
    }
  }

  public componentWillUnmount() {
    const { src, groupId } = this.props;
    queue.dequeue(src, groupId);
  }

  public loadImage(src) {
    if (src && src.length > 0) {
      queue.enqueue({
        source: this.props.groupId,
        imageUrl: src,
        imgRef: this.imgRef,
        callback: err => this.imageDidLoad(err)
      });
    }
  }

  public imageDidLoad(error) {
    if (error && this.props.queuedImageFallbackCallback) {
      this.props.queuedImageFallbackCallback(this);
    } else {
      this.setState({ src: this.props.src });
    }
  }

  private setImgRef(r) {
    this.imgRef = r;
  }

  public render() {
    const { alt, children, className, useBackgroundImage, ratio, serverRender, serverRenderCallback, loaded } = this.props;
    const { src } = this.state;
    const additionalProps = { ...this.passThroughProps, 'data-loaded': Boolean(src) };

    if (useBackgroundImage) {
      let style = src ? { backgroundImage: `url(${src})` } : {};
      if (serverRender && serverRenderCallback) {
        style = serverRenderCallback(style);
      }

      return (
        <UitkFigure className={className} style={style} ratio={ratio} additionalProps={additionalProps}>
          {children}
          {!src && loaded && <UitkImage alt="" placeholderImage={true} />}
        </UitkFigure>
      );
    }

    return (
      <UitkImage
        imgRef={this.setImgRef}
        className={className}
        src={src || undefined}
        alt={alt}
        additionalProps={additionalProps}
        placeholderImage={!src}
      />
    );
  }

  private _getPassthroughProps(props) {
    return withoutKeys(Object.keys(QueuedImage.propTypes)).merge({}, props);
  }
}
